/**
 * RoleFilter - Toggle buttons to show/hide messages by author role
 *
 * Persists filter state to localStorage.
 */

import { useState, useEffect, useCallback } from 'react';

export interface RoleFilterState {
  showUser: boolean;
  showAssistant: boolean;
  showSystem: boolean;
  showTool: boolean;
}

const STORAGE_KEY = 'humanizer:role-filters';

const DEFAULT_FILTERS: RoleFilterState = {
  showUser: true,
  showAssistant: true,
  showSystem: true,
  showTool: true,
};

function loadFilters(): RoleFilterState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
  } catch { /* noop */ }
  return DEFAULT_FILTERS;
}

interface RoleFilterProps {
  filters: RoleFilterState;
  onFiltersChange: (filters: RoleFilterState) => void;
}

export function useRoleFilters() {
  const [filters, setFilters] = useState<RoleFilterState>(loadFilters);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); }
    catch { /* noop */ }
  }, [filters]);

  return { filters, setFilters };
}

export function RoleFilter({ filters, onFiltersChange }: RoleFilterProps) {
  const toggle = useCallback((key: keyof RoleFilterState) => {
    onFiltersChange({ ...filters, [key]: !filters[key] });
  }, [filters, onFiltersChange]);

  return (
    <div className="role-filter" role="group" aria-label="Filter by role">
      <button
        className={`role-filter__btn${filters.showUser ? ' role-filter__btn--active' : ''}`}
        onClick={() => toggle('showUser')}
        aria-pressed={filters.showUser}
        title="Show user messages"
      >
        {'\u{1F464}'}
      </button>
      <button
        className={`role-filter__btn${filters.showAssistant ? ' role-filter__btn--active' : ''}`}
        onClick={() => toggle('showAssistant')}
        aria-pressed={filters.showAssistant}
        title="Show assistant messages"
      >
        {'\u{1F916}'}
      </button>
      <button
        className={`role-filter__btn${filters.showSystem ? ' role-filter__btn--active' : ''}`}
        onClick={() => toggle('showSystem')}
        aria-pressed={filters.showSystem}
        title="Show system messages"
      >
        {'\u2699\uFE0F'}
      </button>
      <button
        className={`role-filter__btn${filters.showTool ? ' role-filter__btn--active' : ''}`}
        onClick={() => toggle('showTool')}
        aria-pressed={filters.showTool}
        title="Show tool messages"
      >
        {'\u{1F527}'}
      </button>
    </div>
  );
}
